import { useState } from 'react'
import {
  ExternalLink,
  Loader2,
  Package,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  ArrowLeft,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDebounce } from '@/components/hooks/use-debounce'
import { formatMoney } from '@/lib/format'
import {
  useCatalogParts,
  useCollectionRuns,
  useCreateCatalogPart,
  useDeleteCatalogPart,
  useImportCatalogCSV,
  useRunCollection,
  useSuppliers,
} from '@/features/parts/hooks'
import type { CatalogPart } from '@/types/parts'

/**
 * CatalogBrowser is the price book page: search parts, import supplier CSV
 * price sheets, run the price collector, and view collection history.
 */
export function CatalogBrowser() {
  const [searchInput, setSearchInput] = useState('')
  const [manufacturer, setManufacturer] = useState('all')
  const [supplierId, setSupplierId] = useState('all')
  // "Load more" raises the page size; the API returns up to `limit` rows.
  const [pageSize, setPageSize] = useState(100)
  const debounced = useDebounce(searchInput, 300)

  const filters = {
    q: debounced || undefined,
    manufacturer: manufacturer !== 'all' ? manufacturer : undefined,
    supplier_id: supplierId !== 'all' ? supplierId : undefined,
    limit: pageSize,
  }
  const { data, isLoading, isError } = useCatalogParts(filters)
  const { data: suppliers = [] } = useSuppliers()
  const { data: runs = [] } = useCollectionRuns()

  const runMutation = useRunCollection()
  const deleteMutation = useDeleteCatalogPart()
  const createMutation = useCreateCatalogPart()

  const [importOpen, setImportOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [deleting, setDeleting] = useState<CatalogPart | null>(null)

  const parts = data?.parts ?? []
  const total = data?.total ?? 0

  return (
    <div className="flex flex-col gap-6">
      <Link to="/service">
        <Button variant="ghost" size="sm" className="-ml-2 gap-2">
          <ArrowLeft className="h-4 w-4" /> Back to Service
        </Button>
      </Link>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-8 w-8" /> Parts Catalog
          </h1>
          <p className="text-muted-foreground">
            Supplier price book: part numbers and prices collected from supplier sites or imported
            from price sheets.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => runMutation.mutate(undefined)}
            disabled={runMutation.isPending}
          >
            {runMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Collect Prices Now
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="mr-2 h-4 w-4" /> Import CSV
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Part
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search part number or name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Select value={manufacturer} onValueChange={setManufacturer}>
          <SelectTrigger className="sm:w-44">
            <SelectValue placeholder="Manufacturer" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All manufacturers</SelectItem>
            {MANUFACTURERS.map((m) => (
              <SelectItem key={m} value={m} className="capitalize">
                {m.replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={supplierId} onValueChange={setSupplierId}>
          <SelectTrigger className="sm:w-48">
            <SelectValue placeholder="Supplier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suppliers</SelectItem>
            {suppliers.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : isError ? (
            <p className="py-6 text-center text-sm text-destructive">Failed to load catalog.</p>
          ) : parts.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">
                No parts found. Run the collector or import a supplier CSV price sheet.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Part number</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Mfr</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead className="text-right">List</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead>Checked</TableHead>
                    <TableHead className="w-20 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parts.map((p) => {
                    const supplier = suppliers.find((s) => s.id === p.supplier_id)
                    return (
                      <TableRow key={p.id}>
                        <TableCell className="font-mono text-xs">{p.part_number}</TableCell>
                        <TableCell className="max-w-72">
                          <span className="block truncate">{p.name}</span>
                        </TableCell>
                        <TableCell>
                          {p.manufacturer ? (
                            <Badge variant="secondary" className="capitalize">
                              {p.manufacturer.replace('_', ' ')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{supplier?.name ?? '—'}</TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {p.list_price ? formatMoney(p.list_price) : '—'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {p.sale_price
                            ? formatMoney(p.sale_price)
                            : p.list_price
                              ? formatMoney(p.list_price)
                              : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {p.last_price_checked_at
                            ? new Date(p.last_price_checked_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          {p.source_url && (
                            <a
                              href={p.source_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-block text-muted-foreground hover:text-foreground"
                              aria-label="Open source page"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            aria-label="Delete part"
                            onClick={() => setDeleting(p)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
              <p className="mt-3 text-xs text-muted-foreground">
                Showing {parts.length} of {total} matching parts.
                {parts.length < total && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-2"
                    onClick={() => setPageSize((n) => n + 100)}
                  >
                    Load More
                  </Button>
                )}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Collection run history */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <RefreshCw className="h-4 w-4" /> Recent price collections
          </CardTitle>
          <CardDescription>
            The scheduled collector refreshes enabled suppliers (default weekly). Manual runs appear
            here too.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">No runs yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Seen</TableHead>
                  <TableHead className="text-right">New</TableHead>
                  <TableHead className="text-right">Updated</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.slice(0, 10).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.supplier_name}</TableCell>
                    <TableCell className="text-sm">{r.source}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          r.status === 'success'
                            ? 'default'
                            : r.status === 'partial'
                              ? 'secondary'
                              : 'destructive'
                        }
                      >
                        {r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{r.parts_seen}</TableCell>
                    <TableCell className="text-right">{r.parts_created}</TableCell>
                    <TableCell className="text-right">{r.parts_updated}</TableCell>
                    <TableCell className="text-right">{r.errors}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(r.started_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <ImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        suppliers={suppliers.map((s) => ({ id: s.id, name: s.name }))}
      />

      <AddPartDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        isPending={createMutation.isPending}
        error={createMutation.error instanceof Error ? createMutation.error.message : null}
        onSubmit={(body) => createMutation.mutate(body, { onSuccess: () => setAddOpen(false) })}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete catalog part?</DialogTitle>
            <DialogDescription>
              Removes “{deleting?.part_number}” from the price book. Existing part lines on work
              orders keep their snapshot.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleting &&
                deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
              }
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const MANUFACTURERS = [
  'mercury',
  'yamaha',
  'suzuki',
  'honda',
  'volvo_penta',
  'evinrude',
  'johnson',
  'indmar',
  'pcm',
  'crusader',
  'yanmar',
]

// --- CSV import dialog ---

function ImportDialog({
  open,
  onOpenChange,
  suppliers,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  suppliers: { id: string; name: string }[]
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* State lives here so it resets each time the dialog closes. */}
        <ImportForm suppliers={suppliers} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}

function ImportForm({
  suppliers,
  onDone,
}: {
  suppliers: { id: string; name: string }[]
  onDone: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [supplierId, setSupplierId] = useState('none')
  const importMutation = useImportCatalogCSV()

  const run = () => {
    if (!file) return
    importMutation.mutate(
      { file, supplier_id: supplierId !== 'none' ? supplierId : undefined },
      { onSuccess: onDone },
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Import price sheet (CSV)</DialogTitle>
        <DialogDescription>
          Columns:{' '}
          <code className="text-xs">
            manufacturer, part_number, name, description, category, list_price, sale_price,
            currency, source_url, availability, fits_note
          </code>
          . Rows upsert on part number, so re-importing a refreshed sheet updates prices.
        </DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 py-1">
        <div className="grid gap-1.5">
          <Label htmlFor="import-supplier">Supplier (optional)</Label>
          <Select value={supplierId} onValueChange={setSupplierId}>
            <SelectTrigger id="import-supplier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No supplier (generic)</SelectItem>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="import-file">CSV file</Label>
          <Input
            id="import-file"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        {importMutation.isSuccess && (
          <div className="rounded-md border p-3 text-sm">
            <p>
              Imported {importMutation.data.created} new, updated {importMutation.data.updated} of{' '}
              {importMutation.data.rows_read} rows.
            </p>
            {importMutation.data.errors.length > 0 && (
              <ul className="mt-1 max-h-32 overflow-y-auto text-xs text-destructive">
                {importMutation.data.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Close
        </Button>
        <Button onClick={run} disabled={!file || importMutation.isPending}>
          {importMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Import
        </Button>
      </DialogFooter>
    </>
  )
}

// --- Manual add-part dialog ---

export interface CatalogPartFormBody {
  part_number: string
  name: string
  manufacturer?: string
  supplier_id?: string
  category?: string
  list_price?: number
  sale_price?: number
  source_url?: string
  fits_note?: string
}

function AddPartDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  error,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (body: CatalogPartFormBody) => void
  isPending: boolean
  error: string | null
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {/* State lives here so it resets each time the dialog closes. */}
        <AddPartForm
          onSubmit={(body) => {
            onSubmit(body)
            onOpenChange(false)
          }}
          onCancel={() => onOpenChange(false)}
          isPending={isPending}
          error={error}
        />
      </DialogContent>
    </Dialog>
  )
}

function AddPartForm({
  onSubmit,
  onCancel,
  isPending,
  error,
}: {
  onSubmit: (body: CatalogPartFormBody) => void
  onCancel: () => void
  isPending: boolean
  error: string | null
}) {
  const [form, setForm] = useState({
    part_number: '',
    name: '',
    manufacturer: '',
    category: '',
    list_price: '',
    sale_price: '',
    source_url: '',
    fits_note: '',
  })
  const { data: suppliers = [] } = useSuppliers()
  const [supplierId, setSupplierId] = useState('none')

  const num = (s: string) => (s && Number(s) > 0 ? Number(s) : undefined)

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add catalog part</DialogTitle>
        <DialogDescription>Manually add a part number and price to the book.</DialogDescription>
      </DialogHeader>
      <div className="grid gap-3 py-1">
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="np-number">Part number</Label>
            <Input
              id="np-number"
              value={form.part_number}
              onChange={(e) => setForm({ ...form, part_number: e.target.value })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="np-name">Name</Label>
            <Input
              id="np-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="np-mfr">Manufacturer</Label>
            <Select
              value={form.manufacturer || 'none'}
              onValueChange={(v) => setForm({ ...form, manufacturer: v === 'none' ? '' : v })}
            >
              <SelectTrigger id="np-mfr">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Other</SelectItem>
                {MANUFACTURERS.map((m) => (
                  <SelectItem key={m} value={m} className="capitalize">
                    {m.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="np-supplier">Supplier</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger id="np-supplier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No supplier</SelectItem>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="np-list">List price ($)</Label>
            <Input
              id="np-list"
              type="number"
              min="0"
              step="0.01"
              value={form.list_price}
              onChange={(e) => setForm({ ...form, list_price: e.target.value })}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="np-sale">Price ($)</Label>
            <Input
              id="np-sale"
              type="number"
              min="0"
              step="0.01"
              value={form.sale_price}
              onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
            />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="np-url">Source URL</Label>
          <Input
            id="np-url"
            value={form.source_url}
            onChange={(e) => setForm({ ...form, source_url: e.target.value })}
            placeholder="https://…"
          />
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!form.part_number || !form.name || isPending}
          onClick={() =>
            onSubmit({
              part_number: form.part_number,
              name: form.name,
              manufacturer: form.manufacturer || undefined,
              supplier_id: supplierId !== 'none' ? supplierId : undefined,
              category: form.category || undefined,
              list_price: num(form.list_price),
              sale_price: num(form.sale_price),
              source_url: form.source_url || undefined,
              fits_note: form.fits_note || undefined,
            })
          }
        >
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Add Part
        </Button>
      </DialogFooter>
    </>
  )
}
